import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Target, Eye, ArrowRight, FlaskConical, GraduationCap,
  Lightbulb, Handshake, CheckCircle, Globe2, BookOpen,
  Brain, Users, Loader2
} from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useSettings } from '../context/SettingsContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

/* ── Icon map for objectives and departments ──────────────────────────────── */
const ICON_MAP = {
  brain:      Brain,
  graduation: GraduationCap,
  book:       BookOpen,
  globe:      Globe2,
  flask:      FlaskConical,
  lightbulb:  Lightbulb,
  handshake:  Handshake,
  users:      Users,
  target:     Target,
};

/* ── Color map for departments ────────────────────────────────────────────── */
const COLOR_MAP = {
  blue:    'bg-blue-100 text-blue-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  violet:  'bg-violet-100 text-violet-700',
  amber:   'bg-amber-100 text-amber-700',
  rose:    'bg-rose-100 text-rose-700',
  teal:    'bg-teal-100 text-teal-700',
  royal:   'bg-royal-100 text-royal-700',
};

/* ── Skeleton ─────────────────────────────────────────────────────────────── */
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded-xl ${className}`} />
);

export default function AboutPage() {
  const { s } = useSettings();

  const [values,     setValues]     = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [team,       setTeam]       = useState([]);
  const [departments,setDepartments]= useState([]);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    api.get('/about/all')
      .then(r => {
        setValues(r.data.values       || []);
        setObjectives(r.data.objectives || []);
        setTeam(r.data.team           || []);
        setDepartments(r.data.departments || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const [teamRef, teamInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [deptRef, deptInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [objRef,  objInView]  = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">

        {/* Hero */}
        <section className="relative bg-hero-gradient py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-6">About SAAI</span>
              <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                SAAI Institute <span className="font-serif italic lowercase tracking-normal mr-1">of</span> Education & Capacity Development
              </h1>
              <p className="text-white/60 text-xl leading-relaxed mb-8">
                A pioneering, data-driven research institute and capacity-building hub across West Africa — bridging AI, psychometrics, and capacity development to drive human flourishing and sustainable development.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Africa', 'West Africa', 'Est. '+s('founded_year','2010')].map(tag => (
                  <span key={tag} className="px-4 py-1.5 glass rounded-full text-sm text-white/80 font-medium border border-white/20">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Mission / Vision ── */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-8">
            <div className="bg-hero-gradient rounded-3xl p-10 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="w-12 h-12 bg-gold-500/20 rounded-xl flex items-center justify-center mb-6">
                <Target size={24} className="text-gold-400" />
              </div>
              <div className="text-gold-400 text-xs font-bold uppercase tracking-widest mb-2">Mission</div>
              <p className="text-white/85 leading-relaxed text-lg">
                {s('mission_statement','To bridge empirical research, artificial intelligence, and behavioural sciences by delivering rigorous educational evaluations, actionable policy analytics, sustainable social enterprise initiatives, and high-impact capacity development across communities and institutions.')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-3xl p-10 border border-gray-100">
              <div className="w-12 h-12 bg-royal-100 rounded-xl flex items-center justify-center mb-6">
                <Eye size={24} className="text-royal-800" />
              </div>
              <div className="text-royal-800 text-xs font-bold uppercase tracking-widest mb-2">Vision</div>
              <p className="text-gray-700 leading-relaxed text-lg">
                {s('vision_statement','To be Africa\'s premier data-driven research institute, pioneering ethical AI, advanced psychometrics, and innovative educational frameworks for human flourishing and sustainable development.')}
              </p>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Tagline</div>
                <p className="text-royal-800 font-display italic font-semibold">
                  "{s('site_tagline','Data-Driven Insights. Human-Centred Solutions.')}"
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Strategic Objectives ── */}
        <section ref={objRef} className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="section-tag mb-4">Strategic Mandate</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-3">Our Strategic Objectives</h2>
              <p className="text-gray-500 max-w-2xl mx-auto">Concrete mandates that signal our capabilities to grant bodies, government partners, and academic institutions.</p>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1,2,3,4].map(i => <Skeleton key={i} className="h-48" />)}
              </div>
            ) : objectives.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">No objectives yet. Seed from Admin → About Page.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
                {objectives.map((o, i) => {
                  const Icon = ICON_MAP[o.icon] || Target;
                  return (
                    <div key={o._id}
                      className={`bg-white rounded-2xl p-6 shadow-card border border-gray-100 hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 ${objInView ? 'animate-fade-up' : 'opacity-0'}`}
                      style={{ animationDelay:`${i*80}ms` }}>
                      <div className="w-12 h-12 bg-royal-50 rounded-xl flex items-center justify-center mb-4">
                        <Icon size={22} className="text-royal-800" />
                      </div>
                      <h3 className="font-display font-semibold text-gray-900 text-sm mb-2 leading-snug">{o.title}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed">{o.description}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ── Institute Structure / Departments ── */}
        <section ref={deptRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="section-tag mb-4">Institute Structure</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-3">Our Departments</h2>
              <p className="text-gray-500 max-w-xl mx-auto">Four integrated departments working in synergy to deliver research, training, innovation, and strategic impact.</p>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 gap-6">
                {[1,2,3,4].map(i => <Skeleton key={i} className="h-52" />)}
              </div>
            ) : departments.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">No departments yet. Seed from Admin → About Page.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6 stagger">
                {departments.map((d, i) => {
                  const Icon  = ICON_MAP[d.icon]  || BookOpen;
                  const color = COLOR_MAP[d.color] || COLOR_MAP.blue;
                  return (
                    <div key={d._id}
                      className={`bg-gray-50 rounded-2xl p-7 border border-gray-100 hover:border-royal-200 hover:shadow-card transition-all duration-300 ${deptInView ? 'animate-fade-up' : 'opacity-0'}`}
                      style={{ animationDelay:`${i*80}ms` }}>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${color}`}>
                        <Icon size={22} />
                      </div>
                      <h3 className="font-display font-semibold text-gray-900 mb-3">{d.title}</h3>
                      <ul className="space-y-2">
                        {(d.items || []).map((item, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                            <CheckCircle size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />{item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ── P.U.R.P.O.S.E. Values ── */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="section-tag mb-4">Core Values</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-3">P.U.R.P.O.S.E.</h2>
              <p className="text-gray-500 max-w-xl mx-auto">The non-negotiable principles guiding SAAI's research, programs, and operations.</p>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1,2,3,4,5,6,7].map(i => <Skeleton key={i} className="h-28" />)}
              </div>
            ) : values.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">No values yet. Seed from Admin → About Page.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {values.map(v => (
                  <div key={v._id} className="bg-white rounded-2xl p-5 shadow-card border border-gray-100 hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 flex gap-4">
                    <div className="w-10 h-10 bg-royal-800 rounded-xl flex items-center justify-center text-gold-400 font-display font-bold text-lg flex-shrink-0">
                      {v.letter}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm mb-1">{v.label}</div>
                      <div className="text-gray-500 text-xs leading-relaxed">{v.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── Leadership Team ── */}
        <section ref={teamRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="section-tag mb-4">Leadership</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-3">Our Team</h2>
              <p className="text-gray-500 max-w-xl mx-auto">
                {s('team_description','An elite interdisciplinary team specializing in quantitative/qualitative research, machine learning, and educational psychology across Nigeria and The Gambia.')}
              </p>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-40" />)}
              </div>
            ) : team.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">No team members yet. Seed from Admin → About Page.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 stagger">
                {team.map((m, i) => (
                  <div key={m._id}
                    className={`bg-gray-50 rounded-2xl p-5 border border-gray-100 hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 ${teamInView ? 'animate-fade-up' : 'opacity-0'}`}
                    style={{ animationDelay:`${i*60}ms` }}>
                    {m.avatar ? (
                      <img src={m.avatar} alt={m.name}
                        className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4" />
                    ) : (
                      <div className="w-16 h-16 bg-royal-800 rounded-2xl flex items-center justify-center text-white font-display font-bold text-xl mx-auto mb-4">
                        {m.initials || m.name?.[0]}
                      </div>
                    )}
                    <div className="text-center">
                      <div className="font-semibold text-gray-900 text-sm">{m.name}</div>
                      <div className="text-royal-800 text-xs font-medium mt-0.5">{m.role}</div>
                      {m.area && <div className="text-gray-400 text-[11px] mt-1 leading-snug">{m.area}</div>}
                      {m.bio  && <p className="text-gray-500 text-xs mt-2 leading-relaxed line-clamp-3">{m.bio}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-16 bg-gold-gradient">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="font-display text-3xl font-bold text-royal-950 mb-4">
              {s('about_cta_title','Commission Research or Partner With Us')}
            </h2>
            <p className="text-royal-800/70 mb-8 text-lg">
              {s('about_cta_subtitle','SAAI works with ministries, NGOs, academic institutions, and international funders. Let\'s build evidence-based solutions together.')}
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/partner" className="btn-royal">Partner With Us <Handshake size={16} /></Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-royal-900 text-royal-950 font-semibold rounded-full hover:bg-royal-900/10 transition-colors">
                Get in Touch <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </div>
  );
}